import { VdFeatureManager, VdTracking } from '@vend/vend.js'
import { VdToastNotificationService } from '@vend/vend.ui'
import angular, { IScope } from 'angular'

import { QuickKeysManager } from '../../managers/quick-keys-manager'
import { RegisterManager } from '../../managers/register-manager'
import { RetailerSettingsManager } from '../../managers/retailer-settings-manager'
import {
  FEATURE_ENABLE_REGISTER_ACCESS_CONTROL,
  PERMISSIONS,
} from '../../constants'
import { UserManager } from '../../managers/user-manager'

/**
 * =======================================
 * SETTINGS CONTROLLER
 * =======================================
 */
class SettingsCtrl {
  private _isQuickKeysEnabled: boolean
  public awaitingServerResponse: $TSFixMe

  constructor(
    private $scope: IScope,
    public quickKeysManager: QuickKeysManager,
    private registerManager: RegisterManager,
    private vdTracking: VdTracking,
    private vdToastNotificationService: VdToastNotificationService,
    private retailerSettingsManager: RetailerSettingsManager,
    private vdFeatureManager: VdFeatureManager,
    private userManager: UserManager
  ) {
    'ngInject'
    this._isQuickKeysEnabled = this.quickKeysManager.isQuickKeysEnabled()
    this.$scope.$on('$destroy', () =>
      this.registerManager.off(
        'activeRegisterChange',
        this._onActiveRegisterChange
      )
    )
    this.retailerSettingsManager.init().then(() => {
      this.registerManager.on(
        'activeRegisterChange',
        this._onActiveRegisterChange,
        this
      )
    })
  }

  /**
   * Model getter / setter for determing if quick keys is enabled for the current register.
   *
   * @method toggleQuickKeysEnabled
   *
   * @param  enable {Boolean}
   *         To enable or disable quick keys for the register.
   *
   * @return {Boolean} True if the quick keys is enabled for the register, false otherwise
   */
  public toggleQuickKeysEnabled(enable: boolean) {
    if (angular.isUndefined(enable)) {
      return this._isQuickKeysEnabled
    }
    this._isQuickKeysEnabled = enable
    this.awaitingServerResponse = true
    return this.registerManager
      .enableQuickKeys(this._isQuickKeysEnabled)
      .then(() =>
        this.vdTracking.trackUserEvent({
          eventName: 'Quick Key Actions',
          metadata: {
            Event: `Quick Keys ${
              this._isQuickKeysEnabled ? 'Enabled' : 'Disabled'
            }`,
          },
        })
      )
      .catch(() => {
        this.vdToastNotificationService.negative({
          message: `There was a problem ${
            this._isQuickKeysEnabled ? 'enabling' : 'disabling'
          } Quick Keys, please try
            again.`,
        })
        this._isQuickKeysEnabled = !this._isQuickKeysEnabled
      })
      .finally(() => {
        this.awaitingServerResponse = false
        return this._isQuickKeysEnabled
      })
  }

  /**
   * Check whether current user has the permission to reset the register status
   *
   * @method canResetRegisterStatus
   */
  public canAccessSettingsPage() {
    if (!this.vdFeatureManager.get(FEATURE_ENABLE_REGISTER_ACCESS_CONTROL)) {
      return true
    }

    return this.userManager.hasPermission(
      PERMISSIONS.SELL_SCREEN_SETTINGS_UPDATE
    )
  }

  /**
   * Reset isQuickKeysEnabled when the active register changes.
   *
   * @private
   * @method _onActiveRegisterChange
   */
  private _onActiveRegisterChange() {
    this._isQuickKeysEnabled = this.quickKeysManager.isQuickKeysEnabled()
  }
}

/**
 * Controller for the settings view.
 *
 * @module webregister.controllers.settings
 */
angular
  .module('webregister.controllers')
  .controller('SettingsCtrl', SettingsCtrl)
